import { useState } from "react";
import {
  Archive,
  ArchiveRestore,
  FolderArchive,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  Sparkles,
} from "lucide-react";
import { cn } from "@/cn";
import type { AssistantThread } from "@ceo-cmd/shared";

export function AssistantRail({
  collapsed,
  onToggleCollapse,
  threads,
  activeThreadId,
  onNewChat,
  onSelectThread,
  onSetThreadArchived,
  onOpenSettings,
  embedded = false,
}: {
  collapsed: boolean;
  onToggleCollapse: () => void;
  threads: AssistantThread[];
  activeThreadId: string | null;
  onNewChat: () => void;
  onSelectThread: (id: string) => void;
  onSetThreadArchived: (id: string, archived: boolean) => void;
  onOpenSettings: () => void;
  /** Full-width layout without collapse chrome (mobile sheet). */
  embedded?: boolean;
}) {
  const showLabels = embedded || !collapsed;
  const [showArchived, setShowArchived] = useState(false);
  const visibleThreads = threads.filter((thread) =>
    showArchived ? thread.status === "archived" : thread.status !== "archived",
  );

  return (
    <aside
      className={cn(
        "flex h-full shrink-0 flex-col bg-surface/40",
        embedded
          ? "w-full border-0"
          : cn(
              "border-r border-border transition-[width] duration-200",
              collapsed ? "w-12" : "w-64",
            ),
      )}
    >
      {!embedded && (
        <div
          className={cn(
            "flex items-center border-b border-border px-3 py-3",
            collapsed ? "justify-center" : "justify-between",
          )}
        >
          {!collapsed && (
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Sparkles className="size-4 text-brand" />
              AI Assistant
            </div>
          )}
          <button
            type="button"
            aria-label="Collapse sidebar"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
            onClick={onToggleCollapse}
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </button>
        </div>
      )}

      <div className={cn("space-y-1 p-2", !embedded && collapsed && "px-1")}>
        <button
          type="button"
          title="New chat"
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted/20",
            !embedded && collapsed && "justify-center px-0",
          )}
          onClick={onNewChat}
        >
          <Plus className="size-4 shrink-0" />
          {showLabels && <span>New chat</span>}
        </button>
        <button
          type="button"
          title="AI Assistant settings"
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted/20",
            !embedded && collapsed && "justify-center px-0",
          )}
          onClick={onOpenSettings}
        >
          <Settings className="size-4 shrink-0" />
          {showLabels && <span>AI Assistant settings</span>}
        </button>
        <button
          type="button"
          title={showArchived ? "Back to chats" : "Archived chats"}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground hover:bg-muted/20",
            showArchived && "bg-brand-soft text-brand",
            !embedded && collapsed && "justify-center px-0",
          )}
          onClick={() => setShowArchived((value) => !value)}
        >
          {showArchived ? (
            <MessageSquare className="size-4 shrink-0" />
          ) : (
            <FolderArchive className="size-4 shrink-0" />
          )}
          {showLabels && <span>{showArchived ? "Chats" : "Archive"}</span>}
        </button>
      </div>

      {showLabels && (
        <div className="flex min-h-0 flex-1 flex-col px-2 pb-3">
          <p className="px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {showArchived ? "Archive" : "Chats"}
          </p>
          <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
            {visibleThreads.map((t) => (
              <li key={t.threadId} className="group flex items-center gap-1">
                <button
                  type="button"
                  className={cn(
                    "min-w-0 flex-1 truncate rounded-md px-2 py-2 text-left text-sm",
                    activeThreadId === t.threadId
                      ? "bg-brand-soft text-brand"
                      : "text-foreground hover:bg-muted/20",
                  )}
                  onClick={() => onSelectThread(t.threadId)}
                  title={t.title}
                >
                  {t.title || "New chat"}
                </button>
                <button
                  type="button"
                  className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                  title={showArchived ? "Restore chat" : "Archive chat"}
                  aria-label={showArchived ? "Restore chat" : "Archive chat"}
                  onClick={() => onSetThreadArchived(t.threadId, !showArchived)}
                >
                  {showArchived ? (
                    <ArchiveRestore className="size-3.5" />
                  ) : (
                    <Archive className="size-3.5" />
                  )}
                </button>
              </li>
            ))}
            {visibleThreads.length === 0 && (
              <li className="px-2 py-2 text-xs text-muted-foreground">
                {showArchived ? "No archived chats" : "No chats yet"}
              </li>
            )}
          </ul>
        </div>
      )}
    </aside>
  );
}
